import {
    faChartNetwork,
    faCheck,
    faGlobe,
    faMinus,
    faSquareQuestion,
    faTimes,
} from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslation } from "react-i18next";
import { StringID } from "userful-chronos-app-common-js/dist/models/common";
import {
    DhcpdConfigServer,
    NetworkConnectionProfile,
    NetworkStaticRouteConfig,
} from "userful-chronos-app-common-js/dist/models/config/networkConnections";
import { ITable, ITableRow } from "userful-chronos-app-common-js/dist/models/table/userfulTableInterface";
import DHCPMenu from "./sources/DHCPMenu";
import NetworkConnectionsMenu from "./sources/NetworkConnectionsMenu";
import NetworkRoutesMenu from "./sources/NetworkRoutesMenu";

export const convertNetworkRoutesToTable = (
    t,
    objects: NetworkStaticRouteConfig[],
    connectionUUID: StringID,
    onEdit: (connection: NetworkStaticRouteConfig) => void,
    onDelete: (connection: NetworkStaticRouteConfig) => void,
): ITable => {
    const table: ITable = {
        id: "NetworkRoutes",
        headers: [],
        rows: [],
        org: { singular: "Shared.item", plural: "Shared.items" },
    };

    table.headers = [
        { id: "destination", component: <>{t("Shared.address")}</>, width: 320 },
        { id: "netmask", component: <>{t("Shared.netmask")}</>, width: 320 },
        { id: "gateway", component: <>{t("Shared.geteway")}</>, width: 320 },
    ];

    for (let i = 0; i < objects.length; i++) {
        const object = objects[i];

        const row: ITableRow = {
            id: `${connectionUUID.value}-${object.destination.value}-${object.netMask.value}-${i}`,
            columns: [
                {
                    id: "destination",
                    component: <p>{object.destination.value}</p>,
                    sortBy: object.destination.value,
                },
                {
                    id: "netmask",
                    component: <p>{object.netMask.value}</p>,
                    sortBy: object.netMask.value,
                },
                {
                    id: "gateway",
                    component: <p>{object.nextHop.value}</p>,
                    sortBy: object.nextHop.value,
                },
            ],
            actionsComponents: [
                <NetworkRoutesMenu
                    value={object}
                    connectionUUID={connectionUUID}
                    onEdit={onEdit}
                    onDelete={onDelete}
                />,
            ],
            isDraggable: false,
            isDroppable: false,
        };
        table.rows.push(row);
    }

    return table;
};

export const convertNetworkConnectionsToTable = (
    t,
    objects: NetworkConnectionProfile[],
    onEdit: (connection: NetworkConnectionProfile) => void,
    onActivation: (connections: NetworkConnectionProfile) => void,
    onDelete: (connection: NetworkConnectionProfile) => void,
): ITable => {
    const table: ITable = {
        id: "NetworkConnections",
        headers: [],
        rows: [],
        org: { singular: "Shared.item", plural: "Shared.items" },
    };

    table.headers = [
        { id: "active", component: <>{t("Shared.active")}</>, width: 160 },
        { id: "name", component: <>{t("Shared.name")}</>, width: 480 },
        { id: "interface", component: <>{t("Shared.interface")}</>, width: 160 },
        { id: "carrier", component: <>{t("Shared.carrier")}</>, width: 160 },
        { id: "linkState", component: <>{t("Shared.linkState")}</>, width: 160, hidden: true },
        { id: "linkSpeed", component: <>{t("Shared.linkSpeed")}</>, width: 160 },
        { id: "method", component: <>{t("Shared.method")}</>, width: 160 },
        { id: "routes", component: <>{t("Shared.routes")}</>, width: 160 },
    ];

    for (let i = 0; i < objects.length; i++) {
        const object = objects[i];

        let active: { icon: any; color: string; str: string } = {
            icon: faSquareQuestion,
            color: "grey",
            str: "Unknown",
        };

        if (object.active) {
            active.icon = faCheck;
            active.color = "green";
            active.str = "Shared.active";
        } else {
            active.icon = faTimes;
            active.color = "red";
            active.str = "Shared.inactive";
        }

        let carrier: { icon: any; color: string; str: string } = {
            icon: faSquareQuestion,
            color: "grey",
            str: "Unknown",
        };

        if (object.device.carrier) {
            carrier.icon = faCheck;
            carrier.color = "green";
            carrier.str = "Shared.active";
        } else {
            carrier.icon = faTimes;
            carrier.color = "red";
            carrier.str = "Shared.inactive";
        }

        const row: ITableRow = {
            id: object.id.value,
            columns: [
                {
                    id: "active",
                    component: (
                        <div className="column-container-l">
                            <FontAwesomeIcon className={`${active.color}-icon`} icon={active.icon} />
                            <p>{t(active.str)}</p>
                        </div>
                    ),
                    sortBy: object.active + "",
                },
                {
                    id: "name",
                    component: (
                        <div className="column-container-l">
                            <FontAwesomeIcon className="blue-icon" icon={faGlobe} />
                            <p>{object.id.value}</p>
                        </div>
                    ),
                    sortBy: object.id.value,
                },
                {
                    id: "interface",
                    component: <p>{object.interfaceID.value}</p>,
                    sortBy: object.interfaceID.value,
                },
                {
                    id: "carrier",
                    component: (
                        <div className="column-container-l">
                            <FontAwesomeIcon className={`${carrier.color}-icon`} icon={carrier.icon} />
                        </div>
                    ),
                    sortBy: object.device.carrier + "",
                },
                {
                    id: "linkState",
                    component: (
                        <p>
                            {object?.device?.linkSpeed && object?.device?.linkSpeed > 0 ? (
                                object.device.linkState
                            ) : (
                                <FontAwesomeIcon icon={faMinus}></FontAwesomeIcon>
                            )}
                        </p>
                    ),
                    sortBy: object.device.linkState + "",
                },
                {
                    id: "linkSpeed",
                    component: (
                        <p>
                            {object?.device?.linkSpeed && object?.device?.linkSpeed > 0 ? (
                                object.device.linkSpeed
                            ) : (
                                <FontAwesomeIcon icon={faMinus}></FontAwesomeIcon>
                            )}
                        </p>
                    ),
                    sortBy: object.device.linkSpeed + "",
                },
                {
                    id: "method",
                    component: (
                        <p>
                            {object.method !== "UNKNOW" ? (
                                t("Shared." + object.method)
                            ) : (
                                <FontAwesomeIcon icon={faMinus}></FontAwesomeIcon>
                            )}
                        </p>
                    ),
                    sortBy: object.method + "",
                },
                {
                    id: "routes",
                    component: (
                        <div className="column-container-l">
                            <FontAwesomeIcon className={`grey-icon`} icon={faChartNetwork} />
                            <p>{object.routingRoutes.staticRoutes.length}</p>
                        </div>
                    ),
                    sortBy: object.routingRoutes.staticRoutes.length + "",
                },
            ],
            actionsComponents: [
                <NetworkConnectionsMenu
                    value={object}
                    onEdit={onEdit}
                    onActivation={onActivation}
                    onDelete={onDelete}
                />,
            ],
            isDraggable: false,
            isDroppable: false,
        };
        table.rows.push(row);
    }

    return table;
};

export const convertDHCPServersToTable = (
    t,
    objects: DhcpdConfigServer[],
    onActivate: (dhcp: DhcpdConfigServer) => void,
    onEdit: (dhcp: DhcpdConfigServer) => void,
): ITable => {
    const table: ITable = {
        id: "DHCP",
        headers: [],
        rows: [],
        org: { singular: "Shared.item", plural: "Shared.items" },
    };

    table.headers = [
        { id: "interface", component: <>{t("Shared.interface")}</> },
        { id: "enabled", component: <>{t("Shared.enabled")}</> },
    ];

    for (let i = 0; i < objects.length; i++) {
        const object = objects[i];

        const row: ITableRow = {
            id: object.interfaceID.value,
            columns: [
                {
                    id: "interface",
                    component: (
                        <div className="column-container-l">
                            <FontAwesomeIcon className="blue-icon" icon={faGlobe} />
                            <p>{object.interfaceID.value}</p>
                        </div>
                    ),
                    sortBy: object.interfaceID.value + "",
                },
                {
                    id: "enabled",
                    component: (
                        <div className="column-container-l">
                            <div
                                onClick={(e) => {
                                    onActivate(object);
                                }}
                                className={`toggle ${object.enabled ? "toggleActive" : null}`}
                            >
                                <div className="toggleCircle"></div>
                            </div>
                        </div>
                    ),
                    sortBy: object.enabled + "",
                },
            ],
            actionsComponents: [<DHCPMenu value={object} onEdit={onEdit} />],
            isDraggable: false,
            isDroppable: false,
        };
        table.rows.push(row);
    }

    return table;
};
